import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { quizAPI } from '../../services/api';
import { getErrorMessage, logErrorForDeveloper } from '../../utils/errorHandler';

const reject = (context, error, fallback, rejectWithValue) => {
  logErrorForDeveloper(context, error);
  return rejectWithValue(getErrorMessage(error, fallback));
};

export const fetchQuizzes = createAsyncThunk('quizzes/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await quizAPI.fetchQuizzes(params);
    return response.data;
  } catch (error) {
    return reject('quizzes/fetchAll', error, 'We could not load quizzes right now. Please try again.', rejectWithValue);
  }
});

export const fetchQuiz = createAsyncThunk('quizzes/fetchOne', async (id, { rejectWithValue }) => {
  try {
    const response = await quizAPI.fetchQuiz(id);
    return response.data;
  } catch (error) {
    return reject('quizzes/fetchOne', error, 'We could not load this quiz. It may have been removed.', rejectWithValue);
  }
});

export const createQuiz = createAsyncThunk('quizzes/create', async (data, { rejectWithValue }) => {
  try {
    const response = await quizAPI.createQuiz(data);
    return response.data;
  } catch (error) {
    return reject('quizzes/create', error, 'Unable to create the quiz. Please check your input and try again.', rejectWithValue);
  }
});

export const updateQuiz = createAsyncThunk('quizzes/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await quizAPI.updateQuiz(id, data);
    return response.data;
  } catch (error) {
    return reject('quizzes/update', error, 'Unable to save your changes. Please try again.', rejectWithValue);
  }
});

export const deleteQuiz = createAsyncThunk('quizzes/delete', async (id, { rejectWithValue }) => {
  try {
    await quizAPI.deleteQuiz(id);
    return id;
  } catch (error) {
    return reject('quizzes/delete', error, 'Unable to delete this quiz right now. Please try again.', rejectWithValue);
  }
});

export const updateQuizStatus = createAsyncThunk('quizzes/updateStatus', async ({ id, status }, { rejectWithValue }) => {
  try {
    const response = await quizAPI.updateQuizStatus(id, { status });
    return response.data;
  } catch (error) {
    return reject('quizzes/updateStatus', error, 'Unable to update the quiz status. Please try again.', rejectWithValue);
  }
});

export const addQuestion = createAsyncThunk('quizzes/addQuestion', async ({ quizId, data }, { rejectWithValue }) => {
  try {
    const response = await quizAPI.addQuestion(quizId, data);
    return response.data;
  } catch (error) {
    return reject('quizzes/addQuestion', error, 'Unable to add this question. Please check your input and try again.', rejectWithValue);
  }
});

export const updateQuestion = createAsyncThunk('quizzes/updateQuestion', async ({ questionId, data }, { rejectWithValue }) => {
  try {
    const response = await quizAPI.updateQuestion(questionId, data);
    return response.data;
  } catch (error) {
    return reject('quizzes/updateQuestion', error, 'Unable to update this question. Please try again.', rejectWithValue);
  }
});

export const deleteQuestion = createAsyncThunk('quizzes/deleteQuestion', async (questionId, { rejectWithValue }) => {
  try {
    await quizAPI.deleteQuestion(questionId);
    return questionId;
  } catch (error) {
    return reject('quizzes/deleteQuestion', error, 'Unable to delete this question. Please try again.', rejectWithValue);
  }
});

export const fetchQuizQuestions = createAsyncThunk('quizzes/fetchQuestions', async (quizId, { rejectWithValue }) => {
  try {
    const response = await quizAPI.getQuizQuestions(quizId);
    return { quizId, questions: response.data };
  } catch (error) {
    return reject('quizzes/fetchQuestions', error, 'We could not load the questions for this quiz.', rejectWithValue);
  }
});

const initialState = {
  quizzes: [],
  currentQuiz: null,
  questions: [],
  loading: false,
  error: null,
};

const quizSlice = createSlice({
  name: 'quizzes',
  initialState,
  reducers: {
    clearQuizError: (state) => { state.error = null; },
    clearCurrentQuiz: (state) => { state.currentQuiz = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuizzes.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchQuizzes.fulfilled, (state, action) => { state.loading = false; state.quizzes = action.payload.data || action.payload; })
      .addCase(fetchQuizzes.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchQuiz.fulfilled, (state, action) => { state.currentQuiz = action.payload; })
      .addCase(createQuiz.fulfilled, (state, action) => { state.quizzes.unshift(action.payload); })
      .addCase(updateQuiz.fulfilled, (state, action) => {
        state.quizzes = state.quizzes.map((q) => q.id === action.payload.id ? action.payload : q);
        state.currentQuiz = action.payload;
      })
      .addCase(deleteQuiz.fulfilled, (state, action) => { state.quizzes = state.quizzes.filter((q) => q.id !== action.payload); })
      .addCase(updateQuizStatus.fulfilled, (state, action) => {
        state.quizzes = state.quizzes.map((q) => q.id === action.payload.id ? action.payload : q);
        state.currentQuiz = action.payload;
      })
      .addCase(fetchQuizQuestions.fulfilled, (state, action) => { state.questions = action.payload.questions || []; })
      .addCase(addQuestion.fulfilled, (state, action) => { state.questions.push(action.payload); })
      .addCase(updateQuestion.fulfilled, (state, action) => {
        state.questions = state.questions.map((q) => q.id === action.payload.id ? action.payload : q);
      })
      .addCase(deleteQuestion.fulfilled, (state, action) => {
        state.questions = state.questions.filter((q) => q.id !== action.payload);
      });
  },
});

export const { clearQuizError, clearCurrentQuiz } = quizSlice.actions;
export default quizSlice.reducer;
